import * as React from 'react';
import { BrowserRouter, Outlet, Navigate, Params, Routes, Route, useLocation, useParams } from 'react-router-dom';
import {
  Four04,
  Logout,
  PageFrame,
  selectLoginStateWaiting,
  selectUser,
  TestPage,
  TransactionRoutes,
} from 'gossamer-front-end';
import posthog from 'posthog-js';
import { useAuth } from 'supertokens-auth';
import { OuterContainer, ScrollToTop } from 'shared-front-end';
import {
  AbsenceRoutes,
  AbsencePeriodUserRoutes,
  AttendanceRoutes,
  ChameleonBanner,
  ChameleonLogin,
  ConvoRoutes,
  EventRoutes,
  EventProfileRoutes,
  FeedbackFormRoutes,
  InstitutionGroupRoutes,
  InstitutionMemberRoutes,
  InstitutionProposalRoutes,
  InstitutionRoutes,
  LocationCheckLogRoutes,
  LocationRoutes,
  LocationTrackingScheduleRoutes,
  OrgUnitRoutes,
  OutboundEmailRoutes,
  PaymentInRoutes,
  PaymentOutRoutes,
  PaymentsRoutes,
  PushNotificationRoutes,
  ReportingRoutes,
  UserRoutes,
  VoucherCodesRoutes,
  getLivestreamRoutes,
  getWebsiteAccountRoutes,
  getWebsiteAuthRoutes,
  getWebsiteEventsRoutes,
  getWebsiteHomeRoutes,
  getWebsiteInstitutionRoutes,
  getWebsiteLivestreamRoutes,
  getWebsiteMarketingRoutes,
  getWebsiteOtherRoutes,
  WorkflowRoutes,
  getWebsiteProtectedEventsRoutes,
} from 'cb-front-end';
import { Home } from './Home';
import { TimetableRoutes } from 'timetable-frontend';
import { useSelector } from 'react-redux';
import { SidebarContainer } from './SidebarContainer';
import { WebsiteContainer } from './WebsiteContainer';
import { CaptureAttendanceQRCode } from 'packages/cb-front-end/website-events/CaptureAttendanceQRCode';

const ProtectedRoutes = () => {
  const initialising = useSelector(selectLoginStateWaiting);
  const user = useSelector(selectUser);
  const location = useLocation();
  if (initialising) {
    // wait for session state to resolve before potentially redirecting...
    return <div>Restoring user session...</div>;
  }
  if (!!user) {
    posthog.identify(user.email, {
      name: user.name,
      role: user.roleId,
      institution: user.orgPath,
    });
    return <Outlet />;
  }
  return <Navigate to={`/auth/login?redirectToPath=${encodeURIComponent(location.pathname)}`} />;
};

const LegacyAuthRedirect = (props: { from: string; to: string; preserveSuffix?: boolean }) => {
  const location = useLocation();
  const suffix = props.preserveSuffix ? location.pathname.slice(props.from.length) : '';
  return <Navigate to={`${props.to}${suffix}${location.search}${location.hash}`} replace />;
};

// Old main-frontend URLs. Sub-paths are dropped because the website pages use tabs, not nested routes.
const LegacyWebsiteRedirect = (props: { to: (params: Readonly<Params>) => string }) => {
  const params = useParams();
  const location = useLocation();
  return <Navigate to={`${props.to(params)}${location.search}${location.hash}`} replace />;
};

export const LoggedInRoutes = (): React.JSX.Element => {
  const { signIn, signOut } = useAuth();
  return (
    <BrowserRouter>
      <ScrollToTop>
        <ChameleonBanner />
        <Routes>
          {getLivestreamRoutes()}
          <Route path="/auth" element={<PageFrame />}>
            {getWebsiteAuthRoutes(signIn)}
            <Route path="logout" element={<Logout signOut={signOut} />} />
            <Route path="email-verification" element={<Navigate to="/auth/login" replace />} />
            <Route path="chameleon-login" element={<ChameleonLogin />} />
            <Route path="*" element={<Four04 />} />
          </Route>
          <Route path="/login" element={<LegacyAuthRedirect from="/login" to="/auth/login" />} />
          <Route
            path="/reset-password"
            element={<LegacyAuthRedirect from="/reset-password" to="/auth/reset-password" />}
          />
          <Route
            path="/registration/user/*"
            element={<LegacyAuthRedirect from="/registration/user" to="/auth/user-registration" preserveSuffix />}
          />
          <Route path="/plan-options" element={<LegacyAuthRedirect from="/plan-options" to="/auth/plan-options" />} />
          <Route path="/" element={<OuterContainer />}>
            <Route element={<WebsiteContainer />}>{getWebsiteHomeRoutes()}</Route>
            <Route element={<ProtectedRoutes />}>{getWebsiteProtectedEventsRoutes()}</Route>
          </Route>
          <Route path="/website" element={<OuterContainer />}>
            <Route element={<WebsiteContainer />}>
              {getWebsiteEventsRoutes()}
              {getWebsiteHomeRoutes()}
              {getWebsiteInstitutionRoutes()}
              {getWebsiteMarketingRoutes()}
              {getWebsiteOtherRoutes()}
              <Route element={<ProtectedRoutes />}>
                {getWebsiteAccountRoutes()}
                {getWebsiteLivestreamRoutes()}
              </Route>
              <Route path="*" element={<Four04 />} />
            </Route>
          </Route>
          <Route path="/admin" element={<OuterContainer />}>
            <Route element={<SidebarContainer />}>
              <Route index element={<Home />} />
              <Route element={<ProtectedRoutes />}>
                <Route path="home" element={<Home />} />
                <Route path="absence-period/*" element={<AbsenceRoutes />} />
                <Route path="absence-period-user/*" element={<AbsencePeriodUserRoutes />} />
                <Route path="attendance/*" element={<AttendanceRoutes />} />
                <Route path="convo/*" element={<ConvoRoutes />} />
                <Route path="event/*" element={<EventRoutes />} />
                <Route path="event-profile/*" element={<EventProfileRoutes />} />
                <Route path="feedback-form/*" element={<FeedbackFormRoutes />} />
                <Route path="institution/*" element={<InstitutionRoutes />} />
                <Route path="institution-group/*" element={<InstitutionGroupRoutes />} />
                <Route path="institution-member/*" element={<InstitutionMemberRoutes />} />
                <Route path="institution-proposal/*" element={<InstitutionProposalRoutes />} />
                <Route path="location/*" element={<LocationRoutes />} />
                <Route path="location-check-log/*" element={<LocationCheckLogRoutes />} />
                <Route path="location-tracking-schedule/*" element={<LocationTrackingScheduleRoutes />} />
                <Route path="org-unit/*" element={<OrgUnitRoutes />} />
                <Route path="outbound-email/*" element={<OutboundEmailRoutes />} />
                <Route path="payments/*" element={<PaymentsRoutes />} />
                <Route path="payments-in/*" element={<PaymentInRoutes />} />
                <Route path="payments-out/*" element={<PaymentOutRoutes />} />
                <Route path="push-notification/*" element={<PushNotificationRoutes />} />
                <Route path="reporting/*" element={<ReportingRoutes />} />
                <Route path="test" element={<TestPage />} />
                <Route path="timetable/*" element={<TimetableRoutes />} />
                <Route path="transaction/*" element={<TransactionRoutes />} />
                <Route path="user/*" element={<UserRoutes />} />
                <Route path="voucher-codes/*" element={<VoucherCodesRoutes />} />
                <Route path="workflow/*" element={<WorkflowRoutes />} />
              </Route>
              <Route path="*" element={<Four04 />} />
            </Route>
          </Route>
          <Route
            path="/account/*"
            element={
              <LegacyWebsiteRedirect
                to={(params) =>
                  `/website/account/${params['*'] === 'cancelled-paid-bookings' ? 'cancelled-bookings' : params['*'] ?? ''}`
                }
              />
            }
          />
          {['about-us', 'contact-us', 'get-started', 'video-guides'].map((page) => (
            <Route key={page} path={`/${page}`} element={<LegacyWebsiteRedirect to={() => `/website/${page}`} />} />
          ))}
          <Route path="/events" element={<LegacyWebsiteRedirect to={() => '/website/events'} />} />
          <Route
            path="/events/:eventSlug/*"
            element={<LegacyWebsiteRedirect to={(params) => `/website/events/${params.eventSlug}`} />}
          />
          <Route
            path="/host-payments-payout-terms"
            element={<LegacyWebsiteRedirect to={() => '/website/host-payments-payout-terms'} />}
          />
          <Route
            path="/institution/:id/*"
            element={<LegacyWebsiteRedirect to={(params) => `/website/institution/${params.id}`} />}
          />

          {/** temp fix for incorrect URL in helpers.ts */}
          <Route path="/attend-qr/:attendanceToken" element={<CaptureAttendanceQRCode />} />

          <Route path="/*" element={<Four04 />} />
        </Routes>
      </ScrollToTop>
    </BrowserRouter>
  );
};
